<template>
<m-pageframe
    title="{organization-label}: Order Attribution Settings"
>
<v-container
    fluid
>

    <v-text-field
        autocomplete="off"
        label="Search"
        prepend-icon="mdi-magnify"
        v-model="full_text"
        v-on:input="update"
    >
        <v-menu
            bottom
            offset-y
            slot="append-outer"
        >
            <template v-slot:activator="{ on }">
                <v-icon
                    v-on="on"
                >
                    mdi-dots-vertical
                </v-icon>
            </template>
    
            <v-list>
                <v-list-item
                    v-on:click="onClickCreate()"
                >
                    <v-list-item-title>Create New Settings...</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu> 
    </v-text-field>

    <v-checkbox
        label="Show archived settings"
        v-model="show_archived_settings"
        style="margin-top: -5px"
        v-on:click="doUpdate"
    />

    <div
        v-if="show_no_settings_instructions"
        style="text-align: center; margin-top: 55px"
    >
        Your organization does not have any order attribution settings.  <a v-on:click="onClickCreate()" style="text-decoration: underline">Click here</a> to create your default order attribution settings.
    </div>

    <v-data-table
        :search="full_text"
        v-if="results != null && !show_no_settings_instructions"
        :headers="headers"
        :items="results"
        :items-per-page="100"
        class="elevation-1"
        :hide-default-footer="true"
        disable-sort
        :footer-props="{
            disableItemsPerPage: false,
            disablePagination: false,
        }"
    >
        <template v-slot:item.is_organization_default="{ item }">
            <v-simple-checkbox 
                :value="item.is_organization_default" 
                disabled
            />
        </template>

        <template v-slot:item.is_archived="{ item }">
            <v-simple-checkbox 
                :value="item.is_archived" 
                disabled
            />
        </template>

         <template v-slot:item.exclude_order_types="{ item }">
            {{prettyExcludeOrderTypes(item.exclude_order_types)}}
        </template>

        <template v-slot:item.action="{ item }">
            <v-menu 
                bottom 
                offset-y  
            >
            
            <template v-slot:activator="{ on }">
                    
                <v-icon 
                    v-on="on"
                >
                    mdi-dots-vertical-circle-outline
                </v-icon>
                    
            </template>
            
            <v-list>
                <v-list-item
                    v-on:click="onClickEdit(item)"
                    :disabled="isSystemDefault(item)"
                >
                    <v-list-item-title>Edit...</v-list-item-title>
                </v-list-item>

                <v-divider/>

                <v-list-item
                    v-on:click="onClickMakeDefault(item)"
                    :disabled="item.is_organization_default"
                >
                    <v-list-item-title>Make default</v-list-item-title>
                </v-list-item>

                <v-list-item
                    v-on:click="onClickToggleArchived(item)"
                    :disabled="isSystemDefault(item)"
                >
                    <v-list-item-title>{{item.is_archived ? 'Unarchive' : 'Archive'}}</v-list-item-title>
                </v-list-item>
            
            </v-list>
            
            </v-menu> 
        </template>
    
    </v-data-table>



</v-container>
</m-pageframe>
</template>

<script>
const _ = require('lodash');

export default {
    data() {
        return {
            full_text: null,
            results_raw: [],
            headers: [
                { text: 'Label', value: 'label'},
                { text: 'Default', value: 'is_organization_default'},
                { text: 'Archived', value: 'is_archived'},
                { text: 'Max Attributed Opt Out Delay (Days)', value: 'opt_out_prox_days'},
                { text: 'Max Attributed Order Delay (Days)', value: 'order_prox_days'},
                { text: 'Excluded Order Types', value: 'exclude_order_types'},
                { text: 'Action', value: 'action'},
            ],
            show_archived_settings: false,
        }
    },

    mounted () {
        this.doUpdate();
    },

    methods: {
        update: _.debounce(function(){
            this.doUpdate();
        }, 751),
        
        doUpdate()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/integrations/crm/order/attribution/settings/get-profile',
            
                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },
            
                'on_success': (data) => {
                        this.results_raw = data.results;
                    },
            
                'on_error': 'Unable to list order attribution settings',
            });
        },
        

        onClickCreate() {
            this.$rc.prompt({
                'title': 'Order Attribution Settings Label',
                'label': 'Enter the label that will be used for these order attribution settings (e.g. No club)',
                'value': 'Change Me!',
                'unset_label': null,
                'is_valid': (value) => { return value != 'RedChirp Default';},
            
                'after_prompt': (value) => {
                    this.onCreate(value);
                },
            });
        },

        onCreate(label)
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/integrations/crm/order/attribution/create-stub-settings',
            
                'params' : {
                        'organization_id': this.$route.params.organization_id,
                        'label': label,
                    },
            
                'on_success': (data) => {
                        this.onClickEdit(data);
                    },
            
                'on_error': 'Unable to create new settings',
            });
        },

        prettyExcludeOrderTypes(exclude_order_types)
        {
            if ( exclude_order_types == null || exclude_order_types.length == 0 )
                return 'none';

            return this.$rc.pretty(exclude_order_types, 'comma_list');
        },

        onClickEdit(item)
        {
            this.$router.push({
                'name': 'settings-organization-order-attribution-one',
                'params' : {
                    'organization_id': this.$route.params.organization_id,
                    'order_attribution_settings_id': item.order_attribution_settings_id
                }
            });
        },

        onClickMakeDefault(item)
        {
            if ( this.isSystemDefault(item) )
            {
                this.makeRedChirpDefaultOrganizationDefault();
                return;
            }

            item.is_organization_default = true;
            this.save(item);
        },

        makeRedChirpDefaultOrganizationDefault()
        {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/integrations/crm/order/attribution/settings/make-red-chirp-default-org-default',
            
                'params' : {
                        'organization_id': this.$route.params.organization_id,
                    },
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to update settings',
            });
        },

        onClickToggleArchived(item)
        {
            item.is_archived = !item.is_archived;
            this.save(item);
        },

        save(item) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/integrations/crm/order/attribution/settings',
            
                'params' : item,
            
                'on_success': (data) => {
                        this.doUpdate();
                    },
            
                'on_error': 'Unable to save order attribution settings',
            });
        },

        isSystemDefault(item)
        {
            return item.order_attribution_settings_id == '00000000-0000-0000-0000-000000000000';
        }
    },

    computed: {
        show_no_settings_instructions() {
            if ( this.results == null ) return false;
            if ( this.full_text != null && this.full_text.trim().length != 0 ) return false;
            return this.results.length == 0;
        },

        results() {
            if ( this.show_archived_settings ) return this.results_raw;

            return this.results_raw
                        .filter((item) => !item.is_archived);
        },
    },
}
</script>

<style lang="scss" scoped>
</style>